import React, { type ComponentPropsWithoutRef, type ReactNode, useState } from 'react';
import { type SupportedLocale } from '@zep-us/i18n';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { CheckIcon, ChevronUpIcon, GlobeIcon } from '@/icons';
import { useLanguageChange } from '@/shared/components/LanguageSelector/hooks/useLanguageChange';
import { useLanguageOptions } from '@/shared/components/LanguageSelector/hooks/useLanguageOptions';
import { type LanguageSelectorProps } from '@/shared/components/LanguageSelector/LanguageSelector';
import { LanguageSelectorTooltip } from '@/shared/components/LanguageSelector/LanguageSelectorTooltip';
import { DropdownMenuItem } from '@/ui';
import { Accordion, AccordionContent, AccordionItem, AccordionTriggerNoIcon } from '@/ui_v3/components/accordion';
import { cn } from '@/utils/tailwind.util';
export function LanguageSelectorWithAccordion(eventProps: LanguageSelectorProps['eventProps']) {
  const [openStatus, setOpenStatus] = useState<'' | 'open'>('');
  const languageOptions = useLanguageOptions();
  const handleOpenStatus = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenStatus(openStatus === 'open' ? '' : 'open');
  };
  return <Accordion value={openStatus} collapsible type={'single'} data-sentry-element="Accordion" data-sentry-component="LanguageSelectorWithAccordion" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
      <AccordionItem value={'open'} data-sentry-element="AccordionItem" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
        <TriggerButton eventProps={eventProps} onClick={handleOpenStatus} isOpen={openStatus === 'open'} data-sentry-element="TriggerButton" data-sentry-source-file="LanguageSelectorWithAccordion.tsx" />
        <AccordionContent className={'max-h-[240px] overflow-scroll bg-neutral px-2xs pb-none pt-2xs'} data-sentry-element="AccordionContent" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
          {languageOptions.map(lo => <MenuItem key={lo.value} eventProps={eventProps} locale={lo.value} label={lo.label} />)}
        </AccordionContent>
      </AccordionItem>
    </Accordion>;
}
const TriggerButton = ({
  isOpen,
  eventProps,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  isOpen: boolean;
  eventProps: LanguageSelectorProps['eventProps'];
}) => {
  const {
    currentLocale
  } = useLanguageChange({
    location: eventProps.location
  });
  return <AccordionTriggerNoIcon className={cn('px-none pl-xs py-none h-[36px] min-h-[36px] ', 'bg-white rounded-2xs gap-xs hover:bg-gray-50 focus:bg-gray-50')} asChild={false} tabIndex={-1} data-sentry-element="AccordionTriggerNoIcon" data-sentry-component="TriggerButton" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
      <LanguageSelectorTooltip isHide={isOpen} data-sentry-element="LanguageSelectorTooltip" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
        <div className={'flex h-full w-full cursor-pointer items-center gap-3xs self-stretch'} {...props}>
          <div className={'flex h-full flex-1 items-center gap-xs'}>
            <GlobeIcon size={'xs'} fill={ICON_COLOR.default} data-sentry-element="GlobeIcon" data-sentry-source-file="LanguageSelectorWithAccordion.tsx" />
            <div className={'text-body-md-medium'}>{currentLocale}</div>
          </div>
          <ChevronUpIcon className={isOpen ? '' : 'rotate-180'} size={'xs'} fill={ICON_COLOR.neutral} data-sentry-element="ChevronUpIcon" data-sentry-source-file="LanguageSelectorWithAccordion.tsx" />
        </div>
      </LanguageSelectorTooltip>
    </AccordionTriggerNoIcon>;
};
const MenuItem = ({
  locale,
  label,
  eventProps
}: ComponentPropsWithoutRef<'div'> & {
  locale: SupportedLocale;
  label: ReactNode;
  eventProps: LanguageSelectorProps['eventProps'];
}) => {
  const {
    currentLocale,
    handleLanguageChange
  } = useLanguageChange(eventProps);
  const isSelected = currentLocale === locale;
  return <DropdownMenuItem className={cn('flex justify-between pr-2xs text-body-md-medium', isSelected ? 'text-strong bg-strong-alternative hover:text-strong focus:text-strong focus:bg-strong-alternative' : 'text-default')} key={locale} onClick={() => {
    void handleLanguageChange(locale);
  }} data-sentry-element="DropdownMenuItem" data-sentry-component="MenuItem" data-sentry-source-file="LanguageSelectorWithAccordion.tsx">
      <span>{label}</span>
      {isSelected && <CheckIcon size={'2xs'} fill={'currentColor'} />}
    </DropdownMenuItem>;
};